import classNames from 'classnames';
import _, { isNil, xor } from 'lodash';
import { DateTime } from 'luxon';
import { Dispatch, FunctionComponent, MouseEventHandler, SetStateAction, useCallback, useId, useMemo, useState } from 'react';
import { ConditionalStyles, TableColumn, TableProps } from 'react-data-table-component';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaFilter } from 'react-icons/fa';
import { Badge, Button, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Navbar } from 'reactstrap';
import { gql, OperationContext, useMutation, useQuery } from 'urql';
import { useCurrentUser } from '../hooks';
import useFilters from '../hooks/useFilters';
import { GraphDeepPartialOverride, RoleName } from '../types';
import { HacpackMutation, HacpackMutationApproveHandlingArguments, HacpackMutationAssignDateToHandlingsArguments, HacpackQuery, HandlingType, Terminal } from './../types/graph';
import CenteredContainer from './CenteredContainer';
import DataTable from './DataTable';
import Loading from './Loading';
import { GridLayout, HorizontalScroll } from './ui';

const handlingsQuery = gql`
query terminal($id: Int!, $types: [HandlingType]) {
    terminal (id: $id) {
        id
        name
        port
        authorityType
        handlings(checkedIn: false,types: $types) {
            id
            idSerchim
            creationDate 
            unitNumber
            checkedIn
            checkedOut
            checkInDate
            shippingAgencyOrCarrier
            loadingUnitSize
            type
            ship
            loadingUnitSize
            boardingShip
            unboardingShip
            voyage
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            qsaNotes
            approval
            shippingCompany
            assignedDate
            serchimShippingCompany
            chemistPrescriptionsUrl
            prescriptions
            
            shipArrival {
                id
                name
                shippingAgency
                ship
                eta
            }
            dangerousGoods {
                unNumber
                adrClass
                adrSecondaryClasses
            }
        }
    }
}
`;

type TerminalHandlingsApprovalQueryData = GraphDeepPartialOverride<HacpackQuery, {
    terminal: Terminal,
}>

type TerminalHandlingsApprovalQueryVaribles = {
    id: number,
    types: HandlingType[]
}

//const signOutMutation = gql<HacpackMutation, {}>`mutation {
//    signOut  {
//        isOk
//    }
//}`;

type TerminalHandlingsApprovalProps = {
    id: number
}

const handlingsTypes: Record<'import' | 'export', HandlingType[]> = {
    import: [HandlingType.Unboarding, HandlingType.TranshipmentUnboarding],
    export: [HandlingType.Boarding]
}

type ApprovalsTableRow = {
    id: number,
    creationDate: Date,
    authNumber: number,
    qsaNotes: string | null,
    prescriptions: string | null,
    booking: string,
    ship: string,
    eta: Date,
    shippingAgency: string,
    shippingCompany: string,
    adrClasses: string[],
    adrSecondaryClasses: string[],
    unNumbers: string[],
    assignedDate: Date | null,
    loadingUnitSize: string,
    chemistPrescriptionsUrl: string | null,
    approved: boolean,
    hasPrescriptions: boolean
}

const TerminalHandlingsApproval = ({ id }: TerminalHandlingsApprovalProps) => {

    //const { url } = useRouteMatch();

    const { hasRole, isSuperuser } = useCurrentUser();

    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

    const [assignDateFormOpen, setAssignDateFormOpen] = useState<boolean>(false);
    const [approveHandlingFormOpen, setApproveHandlingFormOpen] = useState<boolean>(false);

    const [rowToApprove, setRowToApprove] = useState<ApprovalsTableRow | null>(null);

    const [handlingsType, setHandlingsType] = useState<'import' | 'export'>('export');

    const [showFilters, setShowFilters] = useState<boolean>(false);

    const [result, refetch] = useQuery<TerminalHandlingsApprovalQueryData, TerminalHandlingsApprovalQueryVaribles>({
        query: handlingsQuery,
        variables: { id, types: handlingsTypes[handlingsType] },
        requestPolicy: 'network-only'
    });

    const { data, fetching } = result;

    //const history = useHistory();
    const { t } = useTranslation('common');

    const terminal = data?.terminal;
    const handlings = terminal?.handlings;

    const canApprove = isSuperuser || hasRole(RoleName.QSA);
    const canAssignDate = isSuperuser || hasRole(RoleName.DOCUMENTATION);

    const tableRows = useMemo<ApprovalsTableRow[] | null>(() => {
        if (handlings === undefined) {
            return null;
        }

        return handlings.map(({ id, creationDate, idSerchim, qsaNotes, shipArrival, dangerousGoods, assignedDate, serchimShippingCompany, booking, loadingUnitSize, chemistPrescriptionsUrl, prescriptions, approvedByTerminal }) => {
            const { eta, ship, shippingAgency } = shipArrival!;

            const hasPrescriptions = !(prescriptions === null || !(prescriptions?.match(/nessuna( prescrizione| particolare|\.)/i) === null));

            return {
                id,
                creationDate: DateTime.fromISO(creationDate!).toJSDate(),
                authNumber: idSerchim!,
                qsaNotes: qsaNotes ?? null,
                eta: DateTime.fromISO(eta!).toJSDate(),
                assignedDate: isNil(assignedDate) ? null : DateTime.fromISO(assignedDate!).toJSDate(),
                shippingAgency,
                shippingCompany: serchimShippingCompany ?? null,
                ship,
                prescriptions,
                loadingUnitSize,
                booking,
                adrClasses: _(dangerousGoods).map(dg => dg.adrClass).uniq().sort().value(),
                adrSecondaryClasses: _(dangerousGoods).map(dg => dg.adrSecondaryClasses).flatten().uniq().sort().value(),
                unNumbers: _(dangerousGoods).map(dg => dg.unNumber).uniq().sort().value(),
                chemistPrescriptionsUrl,
                hasPrescriptions,
                approved: approvedByTerminal || !hasPrescriptions
            } as ApprovalsTableRow
        })
    }, [t, handlings]);

    const { filtered: filteredRows, form: filtersForm, count: filterCount } = useFilters({
        data: tableRows ?? [], availableFilters: [
            {
                field: 'approved',
                type: 'boolean',
                title: t('Approvati'),
                initialState: canApprove === canAssignDate ? undefined : { value: canAssignDate }
            },
            {
                field: 'ship',
                type: 'list',
                title: t('Nave')
            },
            {
                field: 'shippingCompany',
                type: 'list',
                title: t('Compagnia')
            },
            {
                field: 'shippingAgency',
                type: 'list',
                title: t('Agenzia')
            },
            {
                field: 'adrClasses',
                type: 'list',
                title: t('Classi')
            },
            {
                field: 'adrSecondaryClasses',
                type: 'list',
                title: t('Classi secondarie')
            },
            {
                field: 'unNumbers',
                type: 'list',
                title: t('UN Number')
            },
            {
                field: 'assignedDate',
                selector: (p) => p.assignedDate?.toISOString() ?? null,
                type: 'date',
                title: t('Data assegnata')
            },
            {
                field: 'eta',
                selector: (p) => p.eta.toISOString(),
                type: 'date',
                title: t('Previsto arrivo')
            },
            {
                field: 'loadingUnitSize',
                type: 'list',
                title: t('Dimensioni')
            }
        ]
    });

    const columns = useMemo<TableColumn<ApprovalsTableRow>[]>(() => {
        const columns = [
            {
                name: t('N° Autorizzazione'),
                selector: (col) => col.authNumber,
                sortable: true,
                grow: 1
            },
            {
                name: t('Note QSA'),
                selector: (col) => col.qsaNotes ?? '',
                cell: ({ qsaNotes }) => <pre data-tag="allowRowEvents" style={{ whiteSpace: 'normal' }}>{qsaNotes}</pre>,
                grow: 6,
                sortable: true,
                maxWidth: '80em',
                wrap: true
            },
            {
                name: t('Data assegnata'),
                selector: ({ assignedDate }) => assignedDate?.getTime() ?? 0,
                cell: ({ assignedDate }) => {
                    if (assignedDate === null) {
                        return '-'
                    }

                    const dt = DateTime.fromJSDate(assignedDate);

                    return <time data-tag="allowRowEvents" dateTime={dt.toISODate() ?? undefined}>{dt.toLocaleString(DateTime.DATE_SHORT)}</time>
                },
                sortable: true,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Booking'),
                selector: (col) => col.booking,
                cell: ({ booking }) => <span style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={booking}>{booking}</span>,
                sortable: true,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Nave'),
                selector: (col) => col.ship,
                cell: ({ ship }) => <span data-tag="allowRowEvents" style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={ship}>{ship}</span>,
                sortable: true,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Previsto arrivo nave'),
                selector: (col) => col.eta,
                cell: ({ eta }) => {
                    const dt = DateTime.fromJSDate(eta);

                    return <time data-tag="allowRowEvents" dateTime={dt.toISODate() ?? undefined }>{dt.toLocaleString(DateTime.DATE_SHORT)}</time>
                },
                sortable: true,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Dimensioni'),
                selector: (col) => col.loadingUnitSize,
                sortable: true,
                grow: 1,
                maxWidth: '2.5em'
            },
            {
                name: t('Agenzia'),
                selector: (col) => col.shippingAgency,
                cell: ({ shippingAgency }) => <span data-tag="allowRowEvents" style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={shippingAgency}>{shippingAgency}</span>,
                sortable: true,
                grow: 2,
                maxWidth: '15em'
            },
            {
                name: t('Compagnia'),
                selector: (col) => col.shippingCompany,
                cell: ({ shippingCompany }) => <span data-tag="allowRowEvents" style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={shippingCompany}>{shippingCompany}</span>,
                sortable: true,
                grow: 2,
                maxWidth: '15em'
            },
            {
                name: t('Classi'),
                selector: (col) => col.adrClasses,
                cell: ({ adrClasses }) => <div data-tag="allowRowEvents" style={{ display: 'flex', flexWrap: 'wrap', gap: '.5em' }}>
                    {adrClasses.map(adrClass => <div key={adrClass}>{adrClass}</div>)}
                </div>,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Classi secondarie'),
                selector: (col) => col.adrSecondaryClasses,
                cell: ({ adrSecondaryClasses }) => <div data-tag="allowRowEvents" style={{ display: 'flex', flexWrap: 'wrap', gap: '.5em' }}>
                    {adrSecondaryClasses.map(adrClass => <div data-tag="allowRowEvents" key={adrClass}>{adrClass}</div>)}
                </div>,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('UN Number'),
                selector: (col) => col.unNumbers,
                cell: ({ unNumbers }) => <div data-tag="allowRowEvents" style={{ display: 'flex', flexWrap: 'wrap', gap: '.5em' }}>
                    {unNumbers.map(unNumber => <div data-tag="allowRowEvents" key={unNumber}>{unNumber}</div>)}
                </div>,
                grow: 1,
                maxWidth: '15em'
            },
            {
                name: t('Allegati'),
                cell: ({ chemistPrescriptionsUrl, prescriptions }) =>
                    chemistPrescriptionsUrl === null
                        ? null
                        : <a href={chemistPrescriptionsUrl} title={prescriptions ?? undefined} target="_blank">{t('Parere del Chimico di Porto')}</a>,
                grow: 1,
                maxWidth: '15em'
            }

        ] as TableColumn<ApprovalsTableRow>[];

        if (canApprove) {
            columns.push({
                name: t('Azioni'),
                cell: (row) => !row.approved && <button type="button" className="btn btn-primary" style={{ display: 'inline-block', whiteSpace: 'nowrap' }} onClick={() => setRowToApprove(row)} color="primary">
                    {t('Approva')}
                </button>,
                minWidth: '10em'
            })
        }

        return columns;
    }, [t, canApprove]);

    //const conditionalRow = useMemo(() => {

    //}, []);

    const conditionalRowStyles = useMemo<ConditionalStyles<ApprovalsTableRow>[]>(() => [
        {
            when: ({ hasPrescriptions, approved }) => hasPrescriptions === true && approved === false,
            style: { backgroundColor: 'rgb(255,196,196)' }
        },
        {
            when: ({ hasPrescriptions }) => hasPrescriptions === false,
            style: { backgroundColor: 'rgb(196,255,196)' }
        },
        {
            when: ({ approved, hasPrescriptions }) => hasPrescriptions === true && approved === true,
            style: { backgroundColor: 'rgb(255,255,196)' }
        }
    ], [])

    const toggleShowFilters = useCallback(() => void setShowFilters((showFilters) => !showFilters), [])

    const onSelectedRowsChange = useCallback<NonNullable<TableProps<ApprovalsTableRow>['onSelectedRowsChange']>>(({ selectedRows }) =>
        setSelectedRowIds((selectedRowIds) => {
            const newSelectedRowIds = selectedRows.map(({ id }) => id);

            if (xor(selectedRowIds, newSelectedRowIds).length === 0) {
                return selectedRowIds;
            }

            return newSelectedRowIds
        }), []);

    //const onRowClicked = useCallback<NonNullable<TableProps<ApprovalsTableRow>['onRowClicked']>>(({ id }) => setSelectedRowIds((selectedRowIds) =>
    //    selectedRowIds.includes(id) ? without(selectedRowIds, id) : sortBy([...selectedRowIds, id])
    //), []);

    //const onApproveButtonClicked = useCallback<MouseEventHandler<HTMLButtonElement>>((ev) => {
    //    setApproveHandlingFormOpen(true);
    //}, []);

    const onAssignDateButtonClicked = useCallback<MouseEventHandler<HTMLButtonElement>>((ev) => {
        setAssignDateFormOpen(true);
    }, []);

    const header = <Navbar className="navbar ng-white border-bottom box-shadow" style={{ padding: 0 }} light>
        <HorizontalScroll>
            <div style={{ display: 'flex', flexWrap: 'nowrap', width: 'auto', minWidth: '100%', gap: '1em' }}>
                <div style={{ flex: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', padding: '4px 0 4px 8px' }}>
                    <strong className="text-nowrap" style={{ lineHeight: '38px' }}>{t('Approvazione')}</strong>
                </div>

                <div style={{ flex: 1, flexBasis: 'auto', alignItems: 'center', padding: '4px 0' }}>
                    <ul className="nav nav-pills">
                        <li className="nav-item">
                            <button className={classNames({ 'nav-link': true, active: handlingsType === 'export' })} aria-current="page" type="button" onClick={(ev) => { ev.preventDefault(); setHandlingsType('export') }}>{t('Export')}</button>
                        </li>
                    </ul>
                    {/*<div style={{ display: 'flex', flexWrap: 'nowrap', gap: '1em' }}>*/}
                    {/*    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                    {/*        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={shipInputField.id}>{t('Nave')}</Label>*/}
                    {/*        <input type="text" disabled={fetching} {...shipInputField} />*/}
                    {/*    </div>*/}
                    {/*    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                    {/*        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={arrivalInputField.id}>{t('Previsto arrivo')}</Label>*/}
                    {/*        <input type="date" disabled={fetching || showResult} {...arrivalInputField} />*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>

                <div style={{ display: 'flex', flex: 0, flexWrap: 'nowrap', alignItems: 'center', padding: '4px 8px 4px 0', gap: '.5em' }}>
                    <Button type="button" onClick={onAssignDateButtonClicked} style={{ whiteSpace: 'nowrap' }} color="primary" className="ml-sm-2 form-control" disabled={fetching || selectedRowIds.length === 0}>{t('Assegna data')}</Button>
                    {/*<Button className="ml-sm-2 form-control" onClick={handleCancelButtonClick} disabled={cancelDisabled}>{t('Annulla')}</Button>*/}
                    <button className={classNames({ btn: true, 'btn-secondary': true, active: showFilters })} style={{ whiteSpace: 'nowrap' }} onClick={() => toggleShowFilters()}>
                        <FaFilter /> {t('Filtra')} {(filterCount > 0) ? <Badge>{filterCount}</Badge> : null}
                    </button>
                </div>
            </div>
        </HorizontalScroll>
    </Navbar>;

    const dataTable = useMemo(() => {
        if (filteredRows === null) {
            return null;
        }

        return <DataTable<ApprovalsTableRow>
            selectableRowsVisibleOnly
            onSelectedRowsChange={onSelectedRowsChange}
            columns={columns}
            conditionalRowStyles={conditionalRowStyles}
            data={filteredRows}
            selectableRows={canAssignDate}
            selectableRowDisabled={({ approved }) => !approved}
            striped />
    }, [selectedRowIds, onSelectedRowsChange, columns, conditionalRowStyles, filteredRows])

    const assignDateModalForm = useMemo(() => {
        if (assignDateFormOpen !== true) {
            return null;
        }

        const key = selectedRowIds.join('|')

        return <AssignDateModalForm
            key={key}
            ids={selectedRowIds}
            open={assignDateFormOpen}
            setOpen={setAssignDateFormOpen}
            onDone={() => refetch()}
        />
    }, [selectedRowIds, assignDateFormOpen, setAssignDateFormOpen])


    const approveHandlingModalForm = useMemo(() => {
        if (rowToApprove === null) {
            return null;
        }

        const key = rowToApprove.id;

        return <ApproveHandlingModalForm
            key={key}
            id={selectedRowIds[0]}
            open={rowToApprove !== null}
            setOpen={(open) => open === false && setRowToApprove(null)}
            defaultQsaNotes={rowToApprove.qsaNotes ?? rowToApprove.prescriptions ?? undefined}
            onDone={() => refetch()}
        />
    }, [rowToApprove, approveHandlingFormOpen, setApproveHandlingFormOpen, refetch])

    return <>
        <Helmet>
            {terminal !== undefined && <title>{t(`Approvazione`)} :: {terminal.name} :: {t('app.title')}</title>}
        </Helmet>

        <GridLayout header={header} right={showFilters === true ? filtersForm : null}>
            {fetching ? <CenteredContainer><Loading /></CenteredContainer> : dataTable}
        </GridLayout>

        {assignDateModalForm}

        {approveHandlingModalForm}

        {/*<GridLayout header={header} right={filtersVisible ? filtersForm : null}>
            <Switch>
                <PrivateRoute exact path={`${url}`}>{content}</PrivateRoute>
                <PrivateRoute path={`${url}/:loadingUnitIdentifier`}>{content}</PrivateRoute>
            </Switch>
        </GridLayout>*/}
    </>
}

export default TerminalHandlingsApproval;

type AssignDateFormProps = {
    ids: number[],
    onDone?: (context: Partial<OperationContext>) => void,
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>
}

type AssignDateToHandlingsMutationResponse = Pick<HacpackMutation, 'assignDateToHandlings'>

const pmisImporterMutation = gql<AssignDateToHandlingsMutationResponse, HacpackMutationAssignDateToHandlingsArguments>`
    mutation ($ids: [Long!]!, $date: DateTime!) {
        assignDateToHandlings(ids: $ids, date: $date)  {
            isOk
        }
    }
`;

const AssignDateModalForm: FunctionComponent<AssignDateFormProps> = ({ ids, onDone, open, setOpen }) => {

    const [mutationState, save] = useMutation<AssignDateToHandlingsMutationResponse, HacpackMutationAssignDateToHandlingsArguments>(pmisImporterMutation);

    const { fetching, data } = mutationState;

    const id = useId();

    const { t } = useTranslation('common');

    const { register, handleSubmit, formState } = useForm<Pick<HacpackMutationAssignDateToHandlingsArguments, 'date'>>()

    const { errors } = formState;

    const togglePrompt = useCallback(() => fetching === false && setOpen((open) => !open), [setOpen]);
    const closePrompt = useCallback(() => fetching === false && setOpen(false), [setOpen]);

    const onSubmit = useCallback(async ({ date }: Pick<HacpackMutationAssignDateToHandlingsArguments, 'date'>) => {

        const context = await save({ date, ids });
        const { error } = context;

        if (error !== undefined) {
            alert(error)
        }

        onDone?.(context);
        closePrompt()

    }, [ids]);

    return <Modal isOpen={open} toggle={togglePrompt}>
        <form onSubmit={handleSubmit(onSubmit/*, onError*/)}>
            <ModalHeader>{t(`Assegna data a ${ids.length} container`)}</ModalHeader>
            <ModalBody>
                <Form tag="div">
                    <FormGroup>
                        <Label htmlFor={`date-${id}`}>{t('Data')}</Label>
                        <input id={`date-${id}`} type="datetime-local" className="form-control" disabled={fetching} {...register('date', {
                            valueAsDate: true,
                            required: t('Campo obbligatorio.').toString()
                        })} />
                        {errors.date?.message !== undefined && <div className="invalid-feedback">{errors.date.message}</div>}
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button disabled={fetching} type="submit" color="primary">{t('Conferma')}</Button>{' '}
                <Button color="secondary" onClick={closePrompt}>{t('Annulla')}</Button>
            </ModalFooter>
        </form>
    </Modal>



    //<form onSubmit={handleSubmit(onSubmit)}>
    //    <input type="datetime-local" {...register('date', {
    //        valueAsDate: true,
    //        required: true
    //    })} />
    //</form>;
}

type ApproveHandlingModalFormProps = {
    id: number,
    defaultQsaNotes?: string,
    onDone?: (context: Partial<OperationContext>) => void,
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>
}

type ApproveHandlingMutationResponse = Pick<HacpackMutation, 'assignDateToHandlings'>

const approveHandlingMutation = gql<ApproveHandlingMutationResponse, HacpackMutationApproveHandlingArguments>`
    mutation ($id: Long!, $qsaNotes: String) {
        approveHandling(id: $id, qsaNotes: $qsaNotes)  {
            isOk
        }
    }
`;

const ApproveHandlingModalForm: FunctionComponent<ApproveHandlingModalFormProps> = ({ id, defaultQsaNotes, onDone, open, setOpen }) => {

    const [mutationState, save] = useMutation<ApproveHandlingMutationResponse, HacpackMutationApproveHandlingArguments>(approveHandlingMutation);

    const { fetching, data } = mutationState;

    const componentId = useId();

    const { t } = useTranslation('common');

    const { register, handleSubmit, formState } = useForm<Pick<HacpackMutationApproveHandlingArguments, 'qsaNotes'>>()

    const { errors } = formState;

    const togglePrompt = useCallback(() => fetching === false && setOpen((open) => !open), [setOpen, fetching]);
    const closePrompt = useCallback(() => fetching === false && setOpen(false), [setOpen, fetching]);

    const onSubmit = useCallback(async ({ qsaNotes }: Pick<HacpackMutationApproveHandlingArguments, 'qsaNotes'>) => {
        const res = await save({ qsaNotes, id });

        const { error } = res;

        if (error !== undefined) {
            alert(error);
        }

        onDone?.(res);

        closePrompt();
    }, [id]);

    return <Modal isOpen={open} toggle={togglePrompt}>
        <form onSubmit={handleSubmit(onSubmit/*, onError*/)}>
            <ModalHeader>{t(`Approva container`)}</ModalHeader>
            <ModalBody>
                <Form tag="div">
                    <FormGroup>
                        <Label htmlFor={`qsaNotes-${componentId}`}>{t('Note QSA')}</Label>
                        <textarea id={`qsaNotes-${componentId}`} className="form-control" defaultValue={defaultQsaNotes} disabled={fetching} {...register('qsaNotes')} />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button disabled={fetching} type="submit" color="primary">{t('Conferma')}</Button>{' '}
                <Button color="secondary" onClick={closePrompt}>{t('Annulla')}</Button>
            </ModalFooter>
        </form>
    </Modal>



    //<form onSubmit={handleSubmit(onSubmit)}>
    //    <input type="datetime-local" {...register('date', {
    //        valueAsDate: true,
    //        required: true
    //    })} />
    //</form>;
}